import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowRight, Shield } from 'lucide-react';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [form, setForm] = useState({ email: '', password: '', full_name: '', zip_code: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        navigate('/dashboard');
      } else {
        if (!form.full_name.trim()) { setError('Please enter your full name.'); setLoading(false); return; }
        await register(form.email, form.password, form.full_name, form.zip_code);
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0EEE9] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0D3D4A] flex-col justify-between p-16">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 flex items-center justify-center">
            <span className="text-white font-bold text-sm">CS</span>
          </div>
          <span className="text-white font-semibold text-xl" style={{fontFamily: 'Outfit, sans-serif'}}>Meridian</span>
        </Link>

        <div>
          <h2 className="text-4xl font-medium text-white mb-6 leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
            Transparent pricing.<br />Real savings.
          </h2>
          <p className="text-[#F0EEE9]/70 text-lg mb-12">
            Access cash-pay surgical pricing across 500+ accredited centers nationwide — and see exactly how much you could save.
          </p>
          <div className="space-y-4">
            {[
              "Compare cash prices vs insurance out-of-pocket",
              "AI-powered procedure identification",
              "Travel & lodging cost estimator",
              "Save and track your searches"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 bg-[#0D3D4A] flex items-center justify-center flex-shrink-0">
                  <Shield size={12} className="text-white" />
                </div>
                <span className="text-[#F0EEE9]/60 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[#F0EEE9]/90 text-xs">
          Meridian does not provide medical advice. Always consult your physician.
        </p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-8">
            <h1 className="text-3xl font-medium text-stone-900 mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className="text-stone-500 text-sm">
              {mode === 'login'
                ? "Sign in to your Meridian account to access saved searches and cost comparisons."
                : "Start finding lower-cost surgical options in minutes."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={e => setForm({...form, full_name: e.target.value})}
                  placeholder="John Smith"
                  className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                  data-testid="register-name-input"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                placeholder="you@example.com"
                className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                data-testid="auth-email-input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-12 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                  data-testid="auth-password-input"
                  required
                  minLength={6}
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8B4B8]">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">ZIP Code <span className="text-[#A8B4B8] font-normal">(optional)</span></label>
                <input
                  type="text"
                  value={form.zip_code}
                  onChange={e => setForm({...form, zip_code: e.target.value})}
                  placeholder="e.g. 90210"
                  maxLength={5}
                  className="w-full px-4 py-3 border border-[#A8B4B8] bg-white text-stone-900 text-sm focus:outline-none focus:border-[#0D3D4A] transition-colors"
                  data-testid="register-zip-input"
                />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0D3D4A] text-white py-4 text-sm font-semibold hover:bg-[#0E4557] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="auth-submit-button"
            >
              {loading ? 'Please wait...' : (mode === 'login' ? 'Sign In' : 'Create Account')}
              {!loading && <ArrowRight size={16} />}
            </button>
          </form>

          <div className="mt-6 text-center">
            {mode === 'login' ? (
              <p className="text-sm text-stone-500">
                Don't have an account?{' '}
                <button onClick={() => setMode('register')} className="text-[#0D3D4A] font-medium hover:text-[#0D3D4A]" data-testid="switch-to-register">
                  Sign up free
                </button>
              </p>
            ) : (
              <p className="text-sm text-stone-500">
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-[#0D3D4A] font-medium hover:text-[#0D3D4A]" data-testid="switch-to-login">
                  Sign in
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
